// Shared with ImageUploadField — pulled out so the bulk-create flow can pin
// many files the same way without duplicating the base64/fetch dance.
const MAX_BYTES = 4 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/** Pins one image file to IPFS via the pin-image Netlify function, returning
 * the resulting hash. Throws with a human-readable message on failure (too
 * big, wrong type, upload error) — callers decide how to surface that. */
export async function uploadImageToIpfs(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error(`${file.name} isn't an image file`);
  }
  if (file.size > MAX_BYTES) {
    throw new Error(`${file.name} is too big (${(file.size / (1024 * 1024)).toFixed(1)}MB, max ${MAX_BYTES / (1024 * 1024)}MB)`);
  }
  const dataBase64 = await fileToBase64(file);
  const res = await fetch("/.netlify/functions/pin-image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename: file.name, contentType: file.type, dataBase64 }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `Upload failed for ${file.name}`);
  return json.hash as string;
}
